<template>
  <div class="app-container">
    <h1>Oferty pracy</h1>

    <!-- Filters -->
    <div class="filter-container">
      <el-select v-model="query.status" placeholder="Wszystkie statusy" clearable style="width: 180px;" class="filter-item" @change="fetchList">
        <el-option label="Oczekujące" value="pending" />
        <el-option label="Zaakceptowane" value="approved" />
        <el-option label="Odrzucone" value="rejected" />
      </el-select>
      <el-input v-model="query.search" placeholder="Szukaj (tytuł, firma)" style="width: 250px;" class="filter-item" @keyup.enter.native="fetchList" />
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="fetchList">Szukaj</el-button>
    </div>

    <!-- Table -->
    <el-table v-loading="loading" :data="list" border fit highlight-current-row style="width: 100%" @row-click="showDetail">
      <el-table-column label="ID" width="70" align="center">
        <template slot-scope="{ row }">{{ row.id }}</template>
      </el-table-column>

      <el-table-column label="Tytuł" min-width="200">
        <template slot-scope="{ row }">
          <div style="cursor: pointer;">
            <strong>{{ row.title }}</strong>
            <div v-if="row.salary" style="color: #67c23a; font-size: 12px;">{{ row.salary }}</div>
            <div v-if="row.location" style="color: #909399; font-size: 12px;">{{ row.location }}</div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Firma" width="180">
        <template slot-scope="{ row }">{{ row.company ? row.company.name : '—' }}</template>
      </el-table-column>

      <el-table-column label="Źródło" width="100" align="center">
        <template slot-scope="{ row }">
          <a :href="row.source_url" target="_blank" rel="noopener noreferrer" @click.stop>
            <el-button size="mini" icon="el-icon-link" circle />
          </a>
        </template>
      </el-table-column>

      <el-table-column label="Status" width="140" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="statusTag(row.status)">{{ statusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Data" width="120" align="center">
        <template slot-scope="{ row }">{{ formatDate(row.created_at) }}</template>
      </el-table-column>

      <el-table-column label="Akcje" width="260" align="center">
        <template slot-scope="{ row }">
          <el-button v-if="row.status !== 'approved'" size="mini" type="success" @click.stop="handleApprove(row)">Akceptuj</el-button>
          <el-button v-if="row.status !== 'rejected'" size="mini" type="warning" @click.stop="handleReject(row)">Odrzuć</el-button>
          <el-button size="mini" type="danger" @click.stop="handleDelete(row)">Usuń</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <div style="margin-top: 20px; text-align: center;">
      <el-pagination
        :current-page="query.page"
        :page-size="20"
        :total="total"
        layout="total, prev, pager, next"
        @current-change="handlePageChange"
      />
    </div>

    <!-- Detail modal -->
    <el-dialog :title="detailJob ? detailJob.title : ''" :visible.sync="detailVisible" width="700px" top="5vh">
      <div v-if="detailJob">
        <div style="margin-bottom: 16px;">
          <el-tag :type="statusTag(detailJob.status)" style="margin-right: 8px;">{{ statusLabel(detailJob.status) }}</el-tag>
          <el-tag v-if="detailJob.is_active" type="success" size="small">Aktywna</el-tag>
          <el-tag v-else type="info" size="small">Nieaktywna</el-tag>
        </div>

        <el-descriptions :column="2" border size="medium">
          <el-descriptions-item label="Firma">{{ detailJob.company ? detailJob.company.name : '—' }}</el-descriptions-item>
          <el-descriptions-item label="Wynagrodzenie">{{ detailJob.salary || '—' }}</el-descriptions-item>
          <el-descriptions-item label="Lokalizacja">{{ detailJob.location || '—' }}</el-descriptions-item>
          <el-descriptions-item label="Forma zatrudnienia">{{ detailJob.contract_type || '—' }}</el-descriptions-item>
          <el-descriptions-item label="Data dodania">{{ formatDate(detailJob.created_at) }}</el-descriptions-item>
          <el-descriptions-item label="Data recenzji">{{ detailJob.reviewed_at ? formatDate(detailJob.reviewed_at) : '—' }}</el-descriptions-item>
          <el-descriptions-item label="Źródło" :span="2">
            <a :href="detailJob.source_url" target="_blank" rel="noopener noreferrer" style="color: #409EFF; word-break: break-all;">{{ detailJob.source_url }}</a>
          </el-descriptions-item>
          <el-descriptions-item v-if="detailJob.admin_note" label="Notatka admina" :span="2">
            <span style="color: #F56C6C;">{{ detailJob.admin_note }}</span>
          </el-descriptions-item>
        </el-descriptions>

        <div v-if="detailJob.description" style="margin-top: 20px;">
          <h4 style="margin-bottom: 8px; font-weight: 600;">Opis stanowiska</h4>
          <div style="background: #f5f7fa; border-radius: 6px; padding: 16px; max-height: 400px; overflow-y: auto; line-height: 1.7; white-space: pre-wrap; font-size: 13px;">{{ stripHtml(detailJob.description) }}</div>
        </div>
      </div>
      <span slot="footer">
        <el-button v-if="detailJob && detailJob.status !== 'approved'" type="success" @click="handleApprove(detailJob); detailVisible = false;">Akceptuj</el-button>
        <el-button v-if="detailJob && detailJob.status !== 'rejected'" type="warning" @click="detailVisible = false; handleReject(detailJob);">Odrzuć</el-button>
        <el-button @click="detailVisible = false">Zamknij</el-button>
      </span>
    </el-dialog>

    <!-- Reject dialog -->
    <el-dialog title="Odrzuć ofertę pracy" :visible.sync="rejectDialog.visible" width="500px">
      <p>Powód odrzucenia (opcjonalnie):</p>
      <el-input v-model="rejectDialog.note" type="textarea" :rows="3" placeholder="Np. treść niezgodna z regulaminem..." />
      <span slot="footer">
        <el-button @click="rejectDialog.visible = false">Anuluj</el-button>
        <el-button type="warning" :loading="rejectDialog.loading" @click="confirmReject">Odrzuć</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import waves from '@/directive/waves';
import { getAgencyJobs, approveAgencyJob, rejectAgencyJob, deleteAgencyJob } from '@/api/agency-jobs';

export default {
  name: 'AgencyJobsList',
  directives: { waves },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: {
        page: 1,
        status: '',
        search: '',
      },
      detailVisible: false,
      detailJob: null,
      rejectDialog: {
        visible: false,
        loading: false,
        note: '',
        job: null,
      },
    };
  },
  created() {
    this.fetchList();
  },
  methods: {
    async fetchList() {
      this.loading = true;
      try {
        const res = await getAgencyJobs(this.query);
        this.list = res.data;
        this.total = res.total;
      } catch (e) {
        this.$message.error('Nie udało się pobrać listy ofert pracy');
      } finally {
        this.loading = false;
      }
    },

    showDetail(row) {
      this.detailJob = row;
      this.detailVisible = true;
    },

    async handleApprove(row) {
      try {
        await this.$confirm('Czy zaakceptować tę ofertę pracy?', 'Potwierdzenie', {
          confirmButtonText: 'Akceptuj',
          cancelButtonText: 'Anuluj',
          type: 'info',
        });
        await approveAgencyJob(row.id);
        this.$message.success('Oferta zaakceptowana');
        this.fetchList();
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('Nie udało się zaakceptować oferty');
        }
      }
    },

    handleReject(row) {
      this.rejectDialog.job = row;
      this.rejectDialog.note = '';
      this.rejectDialog.visible = true;
    },

    async confirmReject() {
      this.rejectDialog.loading = true;
      try {
        await rejectAgencyJob(this.rejectDialog.job.id, { admin_note: this.rejectDialog.note });
        this.$message.success('Oferta odrzucona');
        this.rejectDialog.visible = false;
        this.fetchList();
      } catch (e) {
        this.$message.error('Nie udało się odrzucić oferty');
      } finally {
        this.rejectDialog.loading = false;
      }
    },

    async handleDelete(row) {
      try {
        await this.$confirm('Czy na pewno usunąć tę ofertę pracy?', 'Uwaga', {
          confirmButtonText: 'Usuń',
          cancelButtonText: 'Anuluj',
          type: 'warning',
        });
        await deleteAgencyJob(row.id);
        this.$message.success('Oferta usunięta');
        this.fetchList();
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('Nie udało się usunąć oferty');
        }
      }
    },

    handlePageChange(page) {
      this.query.page = page;
      this.fetchList();
    },

    statusTag(status) {
      const map = { pending: 'warning', approved: 'success', rejected: 'danger' };
      return map[status] || 'info';
    },

    statusLabel(status) {
      const map = { pending: 'Oczekuje', approved: 'Zaakceptowana', rejected: 'Odrzucona' };
      return map[status] || status;
    },

    formatDate(date) {
      if (!date) return '—';
      return new Date(date).toLocaleDateString('pl-PL');
    },

    stripHtml(text) {
      if (!text) return '';
      return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    },
  },
};
</script>
